import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LogIn, LogOut, LayoutDashboard, Shield, Stethoscope } from "lucide-react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { NAV } from "../constants/testIds";

export const Navbar = () => {
  const { lang, setLang, user, logout } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t(lang, "nav.home") },
    { to: "/book/ultrasound", label: t(lang, "nav.ultrasound") },
    { to: "/book/opd", label: t(lang, "nav.opd") },
    { to: "/pathology", label: t(lang, "nav.pathology") },
    { to: "/book/xray", label: t(lang, "nav.xray") },
    { to: "/book/ct", label: t(lang, "nav.ct") },
    { to: "/book/mri", label: t(lang, "nav.mri") },
    { to: "/pharmacy", label: t(lang, "nav.pharmacy") },
    { to: "/about", label: t(lang, "nav.about") },
    { to: "/contact", label: t(lang, "nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 font-heading font-bold text-lg">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white">
            <Stethoscope size={20} />
          </div>
          <span>AASAN <span className="text-blue-600">OPD</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {links.map(l => (
            <Link key={l.to} to={l.to} className="px-3 py-2 rounded-full text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid={NAV.langToggle}
            onClick={() => setLang(lang === "hi" ? "en" : "hi")}
            className="px-3 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-medium"
          >
            {lang === "hi" ? "English" : "हिंदी"}
          </button>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {user.role === "admin" && (
                <button data-testid={NAV.admin} onClick={() => navigate("/admin")} className="btn-outline !py-2 !px-4 text-sm">
                  <Shield size={16}/> {t(lang, "nav.admin")}
                </button>
              )}
              {(user.role === "staff" || user.role === "admin") && (
                <button onClick={() => navigate("/staff")} className="btn-outline !py-2 !px-4 text-sm">
                  Staff
                </button>
              )}
              <button data-testid={NAV.dashboard} onClick={() => navigate("/dashboard")} className="btn-outline !py-2 !px-4 text-sm">
                <LayoutDashboard size={16}/> {t(lang, "nav.dashboard")}
              </button>
              <button data-testid={NAV.logout} onClick={logout} className="btn-primary !py-2 !px-4 text-sm">
                <LogOut size={16}/> {t(lang, "nav.logout")}
              </button>
            </div>
          ) : (
            <button
              data-testid={NAV.login}
              onClick={() => navigate("/login")}
              className="btn-primary !py-2 !px-4 text-sm"
            >
              <LogIn size={16}/> {t(lang, "nav.login")}
            </button>
          )}

          <button data-testid={NAV.mobileMenu} onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100">
            {open ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200/70 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {links.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className="px-3 py-2 rounded-lg text-slate-700 hover:bg-blue-50 hover:text-blue-600">
                {l.label}
              </Link>
            ))}
            {user && (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-slate-700 hover:bg-blue-50">
                  {t(lang, "nav.dashboard")}
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-slate-700 hover:bg-blue-50">
                    {t(lang, "nav.admin")}
                  </Link>
                )}
                <button onClick={() => { logout(); setOpen(false); }} className="text-left px-3 py-2 rounded-lg text-red-600 hover:bg-red-50">
                  {t(lang, "nav.logout")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
